import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { trackProductView } from "@/components/shared/RecentlyViewed";
import { motion } from "framer-motion";
import { 
  ArrowLeft, 
  FileText, 
  MessageSquare, 
  Zap, 
  Gauge, 
  Route, 
  Battery, 
  Weight, 
  Circle,
  Clock,
  Ruler,
  ArrowDown,
  RotateCcw,
  Navigation,
  Droplet,
  Settings
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { getProductLineById, getAllProducts, ProductLine, ProductVariant } from "@/data/productLines";
import ProductGallery from "@/components/products/ProductGallery";
import ProductSEO from "@/components/products/ProductSEO";
import VariantToggle from "@/components/products/VariantToggle";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import CTASection from "@/components/shared/CTASection";
import InlineQuoteForm from "@/components/products/InlineQuoteForm";

// Icon mapping for specs
const iconMap: { [key: string]: React.ComponentType<{ className?: string }> } = {
  Zap,
  Gauge,
  Route,
  Battery,
  Weight,
  Circle,
  Clock,
  Ruler,
  ArrowDown,
  RotateCcw,
  Navigation,
  Droplet,
  Settings,
};

const ProductDetail = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const product = getProductLineById(productId || "");
  
  const [activeVariantId, setActiveVariantId] = useState<string>("");

  useEffect(() => {
    if (product && product.variants.length > 0) {
      setActiveVariantId(product.variants[0].id);
      trackProductView({ id: product.id, name: product.name, image: product.image });
    }
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-6 py-32 text-center">
          <h1 className="text-3xl font-bold mb-4">Product Not Found</h1>
          <p className="text-muted-foreground mb-8">
            The product you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate("/products")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Products
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const activeVariant = product.variants.find(v => v.id === activeVariantId) || product.variants[0];
  const relatedProducts = getAllProducts().filter(p => p.id !== product.id && p.category === product.category).slice(0, 3);
  const isElectric = product.powerType === "electric";

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <ProductSEO product={product} activeVariant={activeVariant} />

      {/* Breadcrumb */}
      <div className="container mx-auto px-6 pt-24 pb-4">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/products">Products</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{product.name}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Product Hero Section */}
      <section className="container mx-auto px-6 pb-12">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <ProductGallery images={product.gallery} productName={product.name} />
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="space-y-6"
          >
            {/* Brand & Power Type */}
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant="secondary" className="text-sm">
                {product.brand}
              </Badge>
              <Badge 
                variant={isElectric ? "default" : "outline"}
                className={isElectric 
                  ? "bg-eco-green text-white" 
                  : "bg-amber-500 text-white border-amber-500"
                }
              >
                {isElectric ? "⚡ Electric" : "⛽ Gas"}
              </Badge>
              {isElectric && ["itank", "itango", "ilark", "icart"].includes(product.id) && (
                <Link to="/lemo">
                  <Badge className="bg-eco-green/20 text-eco-green border border-eco-green/30 hover:bg-eco-green/30 transition-colors cursor-pointer">
                    🔋 Available for Rent via LEMO
                  </Badge>
                </Link>
              )}
            </div>

            {/* Name & Tagline */}
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-2">
                {product.name}
              </h1>
              <p className="text-xl text-primary font-medium">
                {product.tagline}
              </p>
            </div>

            {/* Description */}
            <p className="text-muted-foreground leading-relaxed">
              {product.description}
            </p>

            {/* Variant Toggle */}
            {product.variants.length > 1 && (
              <div>
                <p className="text-sm font-medium text-foreground mb-3">Select Variant</p>
                <VariantToggle
                  variants={product.variants}
                  activeVariantId={activeVariantId}
                  onVariantChange={setActiveVariantId}
                />
              </div>
            )}

            {/* Available Colors */}
            {product.colors && product.colors.length > 0 && (
              <div>
                <p className="text-sm font-medium text-foreground mb-3">Available Colors</p>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((color) => (
                    <Badge key={color} variant="outline" className="px-3 py-1">
                      {color}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4 pt-4">
              <Button size="lg" asChild>
                <Link to={`/contact?product=${product.id}&variant=${activeVariantId}`}>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Request Quote
                </Link>
              </Button>
              {product.catalogUrl && (
                <Button size="lg" variant="outline" asChild>
                  <a href={product.catalogUrl} target="_blank" rel="noopener noreferrer">
                    <FileText className="w-4 h-4 mr-2" />
                    Download Catalog
                  </a>
                </Button>
              )}
            </div>

            {/* Inline Quick Quote Form */}
            <InlineQuoteForm
              productName={product.name}
              productId={product.id}
              variantId={activeVariantId}
            />
          </motion.div>
        </div>
      </section>

      {/* Specifications Section */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl font-bold text-foreground mb-2">
              Technical Specifications
            </h2>
            <p className="text-muted-foreground">
              {activeVariant.name} variant specifications
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
          >
            {activeVariant.specs.map((spec, index) => {
              const IconComponent = spec.icon ? iconMap[spec.icon] : Zap;
              return (
                <Card key={index} className="bg-background border-border/50">
                  <CardContent className="p-4 text-center">
                    <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary/10 flex items-center justify-center">
                      {IconComponent && <IconComponent className="w-5 h-5 text-primary" />}
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">{spec.label}</p>
                    <p className="text-lg font-bold text-foreground">{spec.value}</p>
                  </CardContent>
                </Card>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl font-bold text-foreground mb-2">
              Key Features
            </h2>
            <p className="text-muted-foreground">
              What makes the {product.name} special
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {product.commonFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-background border-border/50 hover:border-primary/30 transition-colors">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-10"
            >
              <h2 className="text-3xl font-bold text-foreground mb-2">
                Related Products
              </h2>
              <p className="text-muted-foreground">
                Explore more from our fleet
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {relatedProducts.map((relatedProduct, index) => (
                <motion.div
                  key={relatedProduct.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link to={`/products/${relatedProduct.id}`}>
                    <Card className="overflow-hidden hover:border-primary/30 transition-all group">
                      <div className="aspect-[4/3] bg-gradient-to-b from-muted/30 to-muted/10 p-4">
                        <img
                          src={relatedProduct.image}
                          alt={relatedProduct.name}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <CardContent className="p-4">
                        <Badge variant="secondary" className="mb-2 text-xs">
                          {relatedProduct.brand}
                        </Badge>
                        <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                          {relatedProduct.name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {relatedProduct.tagline}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection
        title={`Interested in the ${product.name}?`}
        description="Get in touch with our team for pricing, customization options, and fleet solutions."
        primaryButtonText="Request a Quote"
        primaryButtonLink={`/contact?product=${product.id}`}
        secondaryButtonText="View Customization"
        secondaryButtonLink="/products/customization"
      />

      <Footer />
    </div>
  );
};

export default ProductDetail;
